/**
 * Applies the newly created/updated product to the active line item.
 *
 * Behavior:
 * - Auto-adds the product to the line item when created from the store,subscription and payment links context.
 * - If editing, preserves the existing amount; otherwise, uses the product's price.
 * - Updates only the active line item based on the provided index.
 *
 * Returns the updated line items array.
 */
import { v4 } from 'uuid';

import { LineItemFormSchemaExtended } from 'src/routes/payments/components/lineItem-editor/lineItem/Controller';
import { LineItemEditorPricingMode } from 'src/routes/payments/components/lineItem-editor/LineItemEditor';
import { LineItemType } from 'src/services/api/invoicesApi';
import { Price, Product } from 'src/services/api/productsApi';
import { arrayToMap } from 'src/utils/array';

export type LineItem = LineItemFormSchemaExtended & {
  priceId?: string;
};

export function getUpdatedLineItemAfterProductCreated({
  lineItems,
  activeLineItemIndex,
  product,
  isEditMode,
  selectedPriceId,
  portalPrices = [],
  mode,
}: {
  lineItems: LineItem[];
  activeLineItemIndex: number | null;
  product: Product;
  isEditMode: boolean;
  selectedPriceId?: string;
  portalPrices: Price[];
  mode?: LineItemEditorPricingMode;
}) {
  if (activeLineItemIndex === null) return lineItems;

  const existingItem = lineItems[activeLineItemIndex];
  const portalPricesMap = arrayToMap(portalPrices, 'ref');
  const isStore =
    mode === LineItemEditorPricingMode.OneTimeStore ||
    mode === LineItemEditorPricingMode.subscriptionsStore;

  // 1. Determine the correct price ID
  let finalPriceId: string | undefined;

  if (selectedPriceId) {
    // Case: User just created a new price in the PriceTable → use it
    finalPriceId = selectedPriceId;
  } else if (existingItem?.priceId) {
    // Case: Editing existing line item → preserve current priceId (most important fix!)
    finalPriceId = existingItem.priceId;
  } else if (product.price?.id) {
    // Case: Product has a default price (rare in edit mode, but safe fallback)
    finalPriceId = product.price.id;
  }
  // If still no priceId → leave as empty (should not happen in valid flows)

  // 2. Determine the amount (in dollars, not cents)
  const selectedPriceObj = selectedPriceId
    ? portalPricesMap.get(selectedPriceId)
    : null;
  const amountFromSelectedPrice = selectedPriceObj
    ? selectedPriceObj.amount / 100
    : null;

  const finalAmount =
    amountFromSelectedPrice?.toString() ??
    (isEditMode && existingItem?.amount
      ? existingItem.amount
      : product.price
        ? (product.price.amount / 100).toString()
        : '');

  const hasCustomStoreDescription =
    !!existingItem?.description && existingItem.description !== '<p></p>';

  const updatedLineItem: LineItemFormSchemaExtended = {
    ...existingItem,
    id: existingItem?.id ?? v4(),
    productId: product.id,
    type: LineItemType.Product,
    // In subscription and payment link flows, the line item description is set to the product name.
    // In the store flow, we seed from product description only when listing copy is still empty.
    ...(isStore
      ? {
          description: hasCustomStoreDescription
            ? existingItem.description
            : (product.description ?? ''),
        }
      : { description: product.name }),
    quantity: existingItem?.quantity ?? '1',
    isValid: true,
    isEditing: false,
    priceId: finalPriceId ?? '',
    amount: finalAmount,
  };

  if (activeLineItemIndex >= 0) {
    return lineItems.map((item, idx) =>
      idx === activeLineItemIndex ? updatedLineItem : item,
    );
  } else {
    return [...lineItems, updatedLineItem];
  }
}
